/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import moment from "moment";
import InputField from "@_common/InputRuleFeild";
import "./CreateModal.scss";
import { getValue } from "@utils/lodash";
import {
  CreateReleaseDate,
  EditReleaseDate,
  GetSpecificLocationData,
} from "@services/checkpoint.service";
import { convertDate } from "@_common/date-helpers";
import { toast } from "react-toastify";
import { sanitizeInput } from "@_common/dompurify";

interface Reminder {
  key: string;
  date: any;
  is_active: boolean;
  manual: boolean;
  id: string;
}

interface RequestState {
  enable_reminder: boolean;
  start_date: any;
  cutoff_date: any;
  reminders: Reminder[];
  vendor_id: string;
  location_id: string;
  notes: string;
  month: string;
  year: string;
}

function CreateModal(props: any) {
  const { toggle, isOpen } = props;
  const [date, setDate] = useState<Date | null>(null);
  const [request, setRequest] = useState<RequestState>({
    enable_reminder: false,
    start_date: "",
    cutoff_date: "",
    reminders: [],
    vendor_id: "",
    location_id: "",
    notes: "",
    month: "",
    year: "",
  });

  const reset = () => {
    setRequest({
      enable_reminder: false,
      start_date: "",
      cutoff_date: "",
      reminders: [],
      vendor_id: "",
      location_id: "",
      notes: "",
      month: "",
      year: "",
    });
  };

  useEffect(() => {
    try {
      if (
        getValue(request, `enable_reminder`, false) &&
        getValue(request, `start_date`, "") &&
        getValue(request, `cutoff_date`, "")
      ) {
        if (modified) {
          setModified(false);
        } else {
          const startDate = moment(getValue(request, `start_date`, ""));
          const endDate = moment(getValue(request, `cutoff_date`, ""));
          const today = moment().startOf("day");
          const isToday = startDate.isSame(today, "day");

          let adjustedStartDate = isToday
            ? startDate.clone().add(1, "days")
            : startDate;
          const gap = endDate.diff(adjustedStartDate, "days");

          if (gap < 0) {
            console.error(
              "Invalid date range: 'cutoff_date' is before 'start_date'."
            );
            return;
          }

          let reminderDates: Reminder[] = [];
          // Changed from 5 to 10 reminders
          const totalReminders = Math.min(10, gap + 1);
          for (let i = 1; i <= totalReminders; i++) {
            const reminderDate = adjustedStartDate
              .clone()
              .add(Math.floor((gap * (i - 1)) / (totalReminders - 1)), "days")
              .format("YYYY-MM-DD");

            // Check if the reminder was manually modified, don't overwrite it
            const existingReminder = request.reminders[i - 1];
            const isManuallyModified =
              existingReminder?.is_active && existingReminder?.manual;
            const mainDate = isManuallyModified
              ? existingReminder.date
              : new Date(reminderDate);

            const normalizedMainDate = new Date(mainDate);
            normalizedMainDate.setHours(0, 0, 0, 0);

            const normalizedCurrentDate = new Date();
            normalizedCurrentDate.setHours(0, 0, 0, 0);
            reminderDates.push({
              key: `reminder_${i}`,
              date: mainDate,
              is_active:
                normalizedMainDate < normalizedCurrentDate
                  ? false
                  : existingReminder?.is_active ?? true,
              manual: isManuallyModified ?? false,
              id: existingReminder?.id || "",
            });
          }
          setRequest((prev) => ({ ...prev, reminders: reminderDates }));
        }
      }
    } catch (error) {
      console.error("Error calculating reminder dates:", error);
    }
  }, [
    request.enable_reminder,
    !request.enable_reminder,
    request.start_date,
    request.cutoff_date,
    getValue(request, `reminders.length`, 0) > 0,
  ]);

  useEffect(() => {
    if (getValue(props, `action`, "") === "edit") {
      getSpecificLocationData(getValue(props, `auditCycle_id`, ""));
    } else {
      reset();
    }
  }, [getValue(props, `action`, "") === "edit"]);

  const [modified, setModified] = useState(false);
  const getSpecificLocationData = async (id: any) => {
    try {
      let resp = await GetSpecificLocationData(id);
      setRequest({
        ...request,
        enable_reminder: getValue(resp, `data.is_reminder`, false),
        start_date: new Date(getValue(resp, `data.start_date`, "")),
        cutoff_date: new Date(getValue(resp, `data.cutoff_date`, "")),
        reminders: getValue(resp, `data.reminder`, []).map((r: any) => ({
          id: r.id,
          date: new Date(getValue(r, `reminder_date`, "")),
          reminder_sent: getValue(r, `reminder_sent`, false),
          is_active: getValue(r, `is_active`, false),
        })),
        notes: getValue(resp, `data.notes`, ""),
      });
      setModified(true);
    } catch (error) {}
  };

  const handleReminderDateChange = (date: string, index: number): void => {
    const updatedReminders = request.reminders.map((reminder, i) =>
      i === index ? { ...reminder, date: date, manual: true } : reminder
    );
    setRequest((prev) => ({ ...prev, reminders: updatedReminders }));
  };

  const handleReminderToggle = (index: number): void => {
    if (!request.enable_reminder) return;

    const updatedReminders = request.reminders.map((reminder, i) => {
      if (i === index) {
        const reminderDate = moment(reminder.date);
        const today = moment().startOf("day");

        if (reminderDate.isBefore(today)) {
          return { ...reminder, is_active: false, manual: true };
        }

        return { ...reminder, is_active: !reminder.is_active, manual: true };
      }
      return reminder;
    });

    setRequest((prev) => ({ ...prev, reminders: updatedReminders }));
  };

  const handleChangeText = (e: any) => {
    const { name, value } = e.target;
    if (name === "enable_reminder") {
      const newEnableReminder = !request.enable_reminder;
      setRequest((prevRequest) => ({
        ...prevRequest,
        enable_reminder: newEnableReminder,
        reminders: prevRequest.reminders.map((reminder) => ({
          ...reminder,
          is_active: newEnableReminder ? reminder.is_active : false,
        })),
      }));
    } else {
      setRequest({
        ...request,
        [name]: sanitizeInput(value),
      });
    }
  };

  const handleChangeRadio = (status: boolean) => {
    setModified(false);
    setRequest((prevRequest) => ({
      ...prevRequest,
      enable_reminder: status,
      reminders: prevRequest.reminders.map((reminder) => ({
        ...reminder,
        is_active: status ? reminder.is_active : false,
      })),
    }));

    setRequest((object: any) => {
      return {
        ...object,
        reminders: object["reminders"].map((x: any, index: number) => {
          const reminderDate = moment(getValue(x, `date`, null));
          const today = moment().startOf("day");
          const isDatePassed = reminderDate.isBefore(today);
          if (!isDatePassed) {
            return {
              ...x,
              is_active: status,
            };
          } else {
            return {
              ...x,
              is_active: false,
            };
          }
        }),
      };
    });
  };

  const handleSubmit = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    const isEditAction = getValue(props, `action`, "") === "edit";
    let payload: any = {
      start_date: convertDate(getValue(request, `start_date`, "")),
      cutoff_date: convertDate(getValue(request, `cutoff_date`, "")),
      is_reminder: getValue(request, `enable_reminder`, false),

      ...(request.reminders &&
        request.reminders.length > 0 && {
          reminders: request.reminders.map((reminder: any) => {
            const reminderObject: any = {
              reminder_date: convertDate(reminder.date),
              is_active: reminder.is_active,
            };
            if (isEditAction && reminder.id) {
              reminderObject.id = reminder.id;
            }
            if (isEditAction) {
              reminderObject.release_audit_cycle = getValue(
                props,
                `auditCycle_id`,
                ""
              );
              reminderObject.vendor = getValue(props, `vendor_id`, "");
            }
            return reminderObject;
          }),
        }),

      month: params.date
        ? parseInt(params.date.split("-")[1], 10)
        : date
        ? parseInt(moment(date).format("MM"), 10)
        : parseInt(moment(dates).format("MM"), 10),

      year: params.date
        ? parseInt(params.date.split("-")[0], 10)
        : date
        ? parseInt(moment(date).format("YYYY"), 10)
        : parseInt(moment(dates).format("YYYY"), 10),

      vendor_id: getValue(props, `vendor_id`, ""),
      location_id: getValue(props, `location_id`, ""),
      notes: getValue(request, `notes`, ""),
    };

    try {
      if (getValue(props, `action`, "") === "edit") {
        let resp = await EditReleaseDate(
          getValue(props, `auditCycle_id`, ""),
          payload
        );
        toast.success(getValue(resp, `message`, ""));
        toggle();
        reset();
        props.getData();
      } else {
        let resp = await CreateReleaseDate(payload);
        toast.success(getValue(resp, `message`, ""));
        toggle();
        reset();
        props.getData();
      }
    } catch (error) {}
  };
  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        centered
        className="rn-create-new-modal"
      >
        <ModalHeader>
          {getValue(props, `action`, "") === "create" ? "Create New " : "Edit"}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="row">
              <div className="col-md-6">
                <label className="rn-dp-vendor-label-wrapper">From</label>
                <div className="position-relative">
                  <label
                    onClick={(e) => e.preventDefault()}
                    className="rn-dp-wrap w-100"
                  >
                    <InputField
                      inputType="DATETYPE-DOB"
                      className=""
                      value={getValue(request, `start_date`, "")}
                      placeholderText="Select From Date"
                      minDate={new Date()}
                      onChange={(e: any) =>
                        setRequest({ ...request, start_date: e })
                      }
                    />
                    <img
                      src="/images/checkpoint/calender.svg"
                      className="img-fluid rn-dp-image-wrap"
                      alt=""
                    />
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <label className="rn-dp-vendor-label-wrapper">To</label>
                <div className="position-relative">
                  <label
                    onClick={(e) => e.preventDefault()}
                    className="rn-dp-wrap w-100"
                  >
                    <InputField
                      inputType="DATETYPE-DOB"
                      className=""
                      value={getValue(request, `cutoff_date`, "")}
                      placeholderText="Select To Date"
                      minDate={getValue(request, `start_date`, new Date())}
                      onChange={(e: any) =>
                        setRequest({ ...request, cutoff_date: e })
                      }
                    />
                    <img
                      src="/images/checkpoint/calender.svg"
                      className="img-fluid rn-dp-image-wrap"
                      alt=""
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-2">
                <div className="rn-reminder-wrap d-flex align-items-center">
                  <label className="rn-vendor-label-wrapper mb-0 me-2">
                    Enable Reminder
                  </label>
                  <div
                    className="custom-switch-wrapper-blue custom-switch-wrapper-icons"
                    style={{ marginTop: "5px" }}
                  >
                    <label className="switch">
                      <input
                        type="checkbox"
                        name="enable_reminder"
                        checked={getValue(request, `enable_reminder`, false)}
                        onChange={() =>
                          handleChangeRadio(
                            !getValue(request, `enable_reminder`, false)
                          )
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {request.reminders.map((reminder, index) => {
              const reminderDate = moment(reminder.date);
              const today = moment().startOf("day");
              const isDatePassed = reminderDate.isBefore(today);

              return (
                <div
                  key={index}
                  className="rn-reminder-list-wrap d-flex align-items-center justify-content-between"
                >
                  <div className="custom-switch-wrapper-blue custom-switch-wrapper-icons">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={reminder.is_active}
                        onChange={() => handleReminderToggle(index)}
                        disabled={isDatePassed}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <label className="rn-vendor-label-wrapper mb-0 mx-5">
                    {`Reminder ${index + 1}`}
                  </label>
                  <div className="rn-dp-wrap-reminder-list">
                    <div className="position-relative">
                      <label
                        onClick={(e) => e.preventDefault()}
                        className="rn-dp-wrap rn-dp-wrap-reminder-list"
                      >
                        <InputField
                          inputType="DATETYPE-DOB"
                          value={reminder.date}
                          placeholderText={`Select Reminder ${index + 1}`}
                          minDate={new Date()}
                          disabled={isDatePassed}
                          onChange={(e: any) =>
                            handleReminderDateChange(e, index)
                          }
                        />
                        <img
                          src="/images/checkpoint/calender.svg"
                          className="img-fluid rn-dp-image-wrap"
                          alt=""
                        />
                      </label>
                    </div>
                  </div>
                  {isDatePassed && (
                    <span className="text-danger ms-2">Reminder is sent</span>
                  )}
                </div>
              );
            })}

            <div className="rn-create-notes-wrapper">
              <label className="rn-vendor-label-wrapper">Notes</label>
              <InputField
                inputType="TEXTAREA"
                placeholder="Enter Notes"
                className="form-control w-100"
                name="notes"
                id="notes"
                value={getValue(request, `notes`, "")}
                onChange={(e: any) => handleChangeText(e)}
                label="Notes"
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="modal-cta-wrap">
            <button
              className="ascent-button ascent-button--primary me-2"
              onClick={() => {
                toggle();
                reset();
              }}
            >
              Cancel
            </button>
            <button
              className="ascent-button ascent-button--secondary"
              onClick={handleSubmit}
            >
              Save
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CreateModal;
